import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Snackbar,
  SnackbarContent,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { signInRequest, signInSuccess } from "../../redux/signin/slice";
import { useNavigate } from "react-router-dom";
import "./signin.css";
import Loading from "../../components/loading/loading";

export default function SignIn() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [usernameError, setUsernameError] = useState(false);
  const [usernameAutoFilled, setUsernameAutoFilled] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [passwordAutoFilled, setPasswordAutoFilled] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorTitle, setErrorTitle] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const dispatch = useDispatch();
  const { response, loading, error } = useSelector(
    (state: any) => state.signIn
  );

  useEffect(() => {
    if (response && !loading) {
      if (response?.mfa) {
        if (response?.mfa?.challengeName === "MFA_SETUP") {
          navigate("/mfa-setup", {
            state: { session: response?.mfa?.session },
          });
        } else {
          navigate("/mfa-otp", {
            state: { session: response?.mfa?.session },
          });
        }
      } else {
        navigate("/all-orders");
      }
      dispatch(signInSuccess({ response: null }));
    }
  }, [response, loading, navigate]); // eslint-disable-line

  useEffect(() => {
    if (error && !loading) {
      setErrorTitle(error.title);
      setErrorMessage(error.detail);
      setShowError(true);
    }
  }, [error, loading]);

  const onUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(event.target.value);
    setUsernameError(false);
  };

  const onPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
    setPasswordError(false);
  };

  const onSignIn = (event: any) => {
    event.preventDefault();
    if (username === "" && password === "") {
      setUsernameError(true);
      setPasswordError(true);
      return;
    }

    if (username === "") {
      setUsernameError(true);
      return;
    }

    if (password === "") {
      setPasswordError(true);
      return;
    }
    dispatch(signInRequest({ username, password }));
  };

  return (
    <Box className="backgroud-layout">
      <Box className="signin-layout">
        <Card className="signin-card">
          <img
            className="signin-logo"
            src={require("../../assets/Vision_Logo.svg").default}
            alt="logo"
          />
          <Typography className="signin-text" variant="h5">
            Sign in to VISION
          </Typography>
          <form onSubmit={onSignIn}>
            <TextField
              className="signin-username"
              margin="normal"
              id="username"
              name="username"
              autoComplete="username"
              label="Username"
              variant="outlined"
              slotProps={{
                inputLabel: { shrink: usernameAutoFilled || !!username },
                htmlInput: {
                  onAnimationStart: (
                    e: React.AnimationEvent<HTMLInputElement>
                  ) => {
                    if (e.animationName === "signin-onAutoFillStart") {
                      setUsernameAutoFilled(true);
                    }
                  },
                },
              }}
              value={username || ""}
              onChange={onUsernameChange}
              error={usernameError}
              helperText={usernameError ? "This field can't be empty" : ""}
            />
            <TextField
              className="signin-password"
              margin="normal"
              id="password"
              name="password"
              autoComplete="current-password"
              label="Password"
              variant="outlined"
              type="password"
              slotProps={{
                inputLabel: { shrink: passwordAutoFilled || !!password },
                htmlInput: {
                  onAnimationStart: (
                    e: React.AnimationEvent<HTMLInputElement>
                  ) => {
                    if (e.animationName === "signin-onAutoFillStart") {
                      setPasswordAutoFilled(true);
                    }
                  },
                },
              }}
              value={password || ""}
              onChange={onPasswordChange}
              error={passwordError}
              helperText={passwordError ? "This field can't be empty" : ""}
            />
            <FormGroup className="signin-checkbox">
              <FormControlLabel
                control={<Checkbox />}
                label="Keep me signed in"
              />
            </FormGroup>
            <Button
              disabled={loading}
              className="signin-button"
              variant="contained"
              onClick={onSignIn}
              type="submit"
            >
              Sign In
            </Button>
          </form>
          <Button
            className="signin-forgot-password"
            variant="text"
            onClick={() => navigate("/forgot-password")}
          >
            Forgot password?
          </Button>
        </Card>
      </Box>
      <Snackbar
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
        open={showError}
        autoHideDuration={3000}
        onClose={() => setShowError(false)}
      >
        <SnackbarContent
          className="signin-snackbar-bg"
          title={errorTitle}
          message={errorMessage}
        />
      </Snackbar>
      {loading && <Loading />}
    </Box>
  );
}
